<!-- src/components/ImageButtonWithTextBelow.vue -->
<template>
  <div class="image-button-column" :style="{ opacity: clickable ? 1 : 0.5 }">
    <button class="image-button" :disabled="!clickable" @click="handleClick">
      <div class="image-button-box">
        <img
          :src="resolvedImage"
          class="image-button-icon"
          :alt="`آیکون ${text}`"
        />
      </div>
    </button>
    <span
      v-if="text"
      class="image-button-text"
      :style="{
        color: isSelected && selectable ? computedColor : INACTIVE_COLOR,
      }"
    >
      {{ text }}
    </span>
  </div>
</template>

<script>
import { globalState } from "../store";
import { computed, watch } from "vue";

/* ✅ Config-driven status colors */
const STATUS_COLORS = {
  deviceInactive: "#e53935", // قرمز
  deviceHalfActive: "#f4c430", // زرد
  deviceActive: "#3efe6c", // سبز
};

const DEFAULT_COLOR = "#3efe6c";
const INACTIVE_COLOR = "#2b2940";

export default {
  name: "ImageButtonWithTextBelow",
  props: {
    groupId: {
      type: String,
      required: true,
    },
    itemId: {
      type: String,
      required: true,
    },
    image: {
      type: String,
      required: true,
    },
    text: {
      type: String,
      default: null,
    },
    defaultSelectedId: {
      type: String,
      default: null,
    },
    selectable: {
      type: Boolean,
      default: true,
    },
    clickable: {
      type: Boolean,
      default: true,
    },
    contentColor: {
      type: String,
      default: null,
    },
  },
  setup(props, { emit }) {
    const baseUrl = computed(() => process.env.BASE_URL || "/");

    const resolvedImage = computed(() => {
      if (!props.image) return "";
      if (
        props.image.startsWith(baseUrl.value) ||
        props.image.startsWith("http")
      ) {
        return props.image;
      }
      const normalizedPath = props.image.startsWith("/")
        ? props.image.slice(1)
        : props.image;
      return `${baseUrl.value}${normalizedPath}`;
    });

    const selectedItemId = computed({
      get() {
        return globalState.getRadioGroupState(
          props.groupId,
          props.defaultSelectedId
        );
      },
      set(value) {
        globalState.setRadioGroupState(props.groupId, value);
      },
    });

    watch(
      () => props.defaultSelectedId,
      (newValue) => {
        if (newValue !== undefined && newValue !== null) {
          selectedItemId.value = newValue;
        }
      },
      { immediate: true }
    );

    const isSelected = computed(() => selectedItemId.value === props.itemId);

    const computedColor = computed(() => {
      return props.contentColor || STATUS_COLORS[props.itemId] || DEFAULT_COLOR;
    });

    const handleClick = () => {
      if (props.clickable) {
        selectedItemId.value = props.itemId;
        emit("item-selected", props.itemId);
      }
    };

    return {
      resolvedImage,
      isSelected,
      handleClick,
      computedColor,
      INACTIVE_COLOR,
    };
  },
};
</script>

<style scoped>
.image-button-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px;
}

.image-button {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid;
  border-color: v-bind(
    "isSelected && selectable ? computedColor : INACTIVE_COLOR"
  );
  background-color: transparent;
  padding: 0;
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;
  overflow: hidden;
}

.image-button:disabled {
  background-color: transparent;
  cursor: not-allowed;
}

.image-button:hover:not(:disabled) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.image-button-box {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-button-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.image-button-text {
  margin-top: 4px;
  font-size: 12px;
  text-align: center;
  transition: color 0.2s;
}
</style>
